import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnChanges, SimpleChanges, inject, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { GrowthJourneyMonitoring, GrowthJourneyService } from './growth-journey.service';
import {
  growthJourneyOnTrackStatusI18nKey,
  growthJourneyOnTrackStatusSeverity as resolveOnTrackSeverity
} from './growth-status.util';

@Component({
  standalone: true,
  selector: 'ain-growth-journey-monitoring-panel',
  imports: [InfoHintComponent, AinStateComponent, ButtonModule, TableModule, TagModule],
  template: `
    <section id="monitoring" class="monitoring-panel card">
      <h3>{{ t('growth.journey.monitoring.title') }}</h3>

      @if (!available) {
        <ain-info-hint
          [titleKey]="'growth.journey.monitoring.unavailable.title'"
          [descriptionKey]="'growth.journey.monitoring.unavailable.description'" />
      } @else {
        <ain-info-hint
          [titleKey]="'growth.journey.monitoring.eg13Hint.title'"
          [descriptionKey]="'growth.journey.monitoring.eg13Hint.description'"
          [bulletKeys]="['growth.journey.monitoring.eg13Boundary']" />

        @if (loading()) {
          <ain-state type="loading" [title]="t('common.loading')" />
        } @else if (error()) {
          <ain-state type="error" [title]="t('common.error')" [message]="error() ?? ''" />
        } @else if (monitoring(); as data) {
          @if (!readOnly) {
            <div class="toolbar">
              <button
                pButton
                type="button"
                severity="secondary"
                [label]="t('common.refresh')"
                [loading]="loading()"
                (click)="load()"></button>
            </div>
          }

          <div class="meta-row">
            <span class="meta-label">{{ t('growth.journey.monitoring.overallStatus') }}</span>
            <p-tag
              [value]="t(onTrackLabelKey(data.overallStatus))"
              [severity]="onTrackSeverity(data.overallStatus)" />
          </div>

          <div class="visibility-card">
            <h4>{{ t('growth.journey.monitoring.visibility.title') }}</h4>
            <div class="visibility-grid">
              <div class="metric">
                <span class="metric-label">{{ t('growth.journey.monitoring.visibility.views') }}</span>
                <strong>{{ data.visibility.totalViews }}</strong>
              </div>
              <div class="metric">
                <span class="metric-label">{{ t('growth.journey.monitoring.visibility.clicks') }}</span>
                <strong>{{ data.visibility.totalClicks }}</strong>
              </div>
              <div class="metric">
                <span class="metric-label">{{ t('growth.journey.monitoring.visibility.leads') }}</span>
                <strong>{{ data.visibility.totalLeads }}</strong>
              </div>
              <div class="metric">
                <span class="metric-label">{{ t('growth.journey.monitoring.visibility.conversionRate') }}</span>
                <strong>{{ formatRate(data.visibility.conversionRate) }}</strong>
              </div>
            </div>
            <div class="meta-row">
              <p-tag
                [value]="providerModeLabel(data.visibility.providerMode)"
                [severity]="data.visibility.providerMode === 'sandbox' ? 'warn' : 'success'" />
              @if (data.visibility.hasSandboxChannels) {
                <p-tag [value]="t('growth.journey.monitoring.visibility.sandboxChannels')" severity="warn" />
              }
            </div>
          </div>

          @if (data.kpis.length > 0) {
            <h4>{{ t('growth.journey.monitoring.kpi.title') }}</h4>
            <p-table [value]="data.kpis">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('growth.journey.monitoring.kpi.name') }}</th>
                  <th>{{ t('growth.journey.monitoring.kpi.target') }}</th>
                  <th>{{ t('growth.journey.monitoring.kpi.actual') }}</th>
                  <th>{{ t('growth.journey.monitoring.kpi.attainment') }}</th>
                  <th>{{ t('growth.journey.monitoring.kpi.status') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.name || row.metricKey }}</td>
                  <td>{{ formatMetric(row.target, row.unit) }}</td>
                  <td>{{ formatMetric(row.actual, row.unit) }}</td>
                  <td>{{ formatPct(row.attainmentPct) }}</td>
                  <td>
                    <p-tag
                      [value]="t(onTrackLabelKey(row.status))"
                      [severity]="onTrackSeverity(row.status)" />
                  </td>
                </tr>
              </ng-template>
            </p-table>
          } @else {
            <ain-state type="empty" [title]="t('growth.journey.monitoring.kpi.empty')" />
          }
        }
      }
    </section>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; display: grid; gap: 10px; margin-bottom: 12px; }
    .card h3, .card h4 { margin: 0; font-size: 1rem; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .meta-row { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .meta-label { font-weight: 600; }
    .visibility-card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 10px; display: grid; gap: 10px; background: #f9fafb; }
    .visibility-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 10px; }
    .metric { display: grid; gap: 4px; }
    .metric-label { color: #6b7280; font-size: 0.85rem; }
  `]
})
export class GrowthJourneyMonitoringPanelComponent implements OnChanges {
  private readonly journeyService = inject(GrowthJourneyService);
  private readonly i18n = inject(LocalizationService);

  @Input({ required: true }) journeyId!: string;
  @Input() available = false;
  @Input() readOnly = false;

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly monitoring = signal<GrowthJourneyMonitoring | null>(null);

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['journeyId'] || changes['available']) && this.available && this.journeyId) {
      this.load();
    }
  }

  load(): void {
    if (!this.journeyId || !this.available) {
      return;
    }

    this.loading.set(true);
    this.error.set(null);
    this.journeyService.getMonitoring(this.journeyId).subscribe({
      next: item => {
        this.monitoring.set(item);
        this.loading.set(false);
      },
      error: err => {
        this.error.set(this.resolveError(err));
        this.loading.set(false);
      }
    });
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }

  onTrackLabelKey(status: unknown): string {
    return growthJourneyOnTrackStatusI18nKey(status);
  }

  onTrackSeverity(status: unknown): 'secondary' | 'info' | 'success' | 'warn' | 'danger' | 'contrast' {
    return resolveOnTrackSeverity(status);
  }

  providerModeLabel(mode: string): string {
    const key =
      mode?.toLowerCase() === 'sandbox'
        ? 'growth.journey.monitoring.providerMode.sandbox'
        : 'growth.journey.monitoring.providerMode.platform';
    return this.t(key);
  }

  formatRate(value: number): string {
    return `${(value * 100).toFixed(1)}%`;
  }

  formatPct(value: number): string {
    return `${value.toFixed(1)}%`;
  }

  formatMetric(value: number, unit: string): string {
    const formatted = unit === 'percent' || unit === '%' ? this.formatPct(value) : String(value);
    return unit && unit !== 'percent' && unit !== '%' ? `${formatted} ${unit}` : formatted;
  }

  private resolveError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const body = error.error as Record<string, unknown> | null;
      if (body && typeof body['error'] === 'string') {
        return this.t(body['error']);
      }
    }

    return this.t('common.error');
  }
}
